//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { useGetYouTubeConnectionStatusQuery } from '@account-settings/components/social-media-accounts/youtube/youtubeAccountSettingsQueries';
import getShouldRefreshFbConnection from '@account-settings/redux/facebook-instagram/selectors/getShouldRefreshFbConnection';
import getShouldReselectIgAccount from '@account-settings/redux/facebook-instagram/selectors/getShouldReselectIgAccount';
import { ICuralateStore } from '@models/AppState';
import { SocialNetwork } from '@models/SocialNetwork';
import { useGetYouTubeUserQuery } from '@universal-scheduling/queries/universalSchedulerQueries';
import { getIsYouTubeAutomaticSchedulingEnabled } from '@universal-scheduling/redux/scheduler/selectors/getIsYouTubeAutomaticSchedulingEnabled';
import { isDefined } from '@utils/filterPredicates';

export const getSocialNetworksUserInfo = (socialNetworks: SocialNetwork[]) =>
  createSelector(
    (state: ICuralateStore) => state.facebookInstagramSettings.flags,
    (state: ICuralateStore) => state.facebook.pageDetails,
    getShouldRefreshFbConnection,
    (state: ICuralateStore) => state.instagram.account,
    getShouldReselectIgAccount,
    (state: ICuralateStore) => state.pinterest.user,
    (state: ICuralateStore) => state.tiktok.user,
    (state: ICuralateStore) => state.twitter.user,
    (state: ICuralateStore) => state.accountSettings.socialMedia,
    (
      { isFetchingConnectedFacebookPageToken, isFetchingFacebookUserToken },
      {
        flags: { isFetchingPageMetadata: isFetchingFacebookPageMetadata },
        pageMetadata: facebookPageMetadata,
      },
      isFacebookStale,
      {
        defaultInstagramAccount: instagramUser,
        isFetchingDefaultInstagramAccount,
      },
      isInstagramStale,
      {
        flags: { isFetchingUser: isFetchingPinterestUser },
        user: pinterestUser,
      },
      { flags: { isFetchingTikTokUser }, user: tiktokUser },
      { flags: { isFetchingTwitterUser }, user: twitterUser },
      {
        pinterest: {
          flags: { isFetchingSettings: isFetchingPinterestSettings },
          hasStaleCredentials: isPinterestStale,
        },
        tiktok: {
          flags: { isFetchingSettings: isFetchingTikTokSettings },
          hasStaleCredentials: isTikTokStale,
        },
        twitter: {
          flags: { isFetchingSettings: isFetchingTwitterSettings },
        },
      },
    ) => {
      // TODO: PD-264381: Cleanup youtube_automatic_posting feature flag
      const isYouTubeAutomaticSchedulingEnabled = useSelector(
        getIsYouTubeAutomaticSchedulingEnabled,
      );
      const {
        data: youtubeConnectionInfo,
        isLoading: isLoadingYouTubeConnectionInfo,
      } = useGetYouTubeConnectionStatusQuery(
        isYouTubeAutomaticSchedulingEnabled,
      );
      const { data: youtubeUserInfo, isLoading: isLoadingYouTubeUserInfo } =
        useGetYouTubeUserQuery(isYouTubeAutomaticSchedulingEnabled);

      const getSocialNetworkUserInfo = (socialNetwork: SocialNetwork) => {
        switch (socialNetwork) {
          case SocialNetwork.Facebook: {
            return {
              bio: facebookPageMetadata.biography,
              followerCount: undefined,
              fullName: undefined,
              username: facebookPageMetadata.username,
              profileImageUrl: facebookPageMetadata.profilePictureUrl,
              isFetched: isDefined(facebookPageMetadata.username),
              isFetching:
                isFetchingFacebookPageMetadata ||
                isFetchingConnectedFacebookPageToken ||
                isFetchingFacebookUserToken,
              isStale: isFacebookStale ?? false,
            };
          }
          case SocialNetwork.Instagram: {
            return {
              bio: instagramUser?.bio,
              followerCount: instagramUser?.followerCount,
              fullName: instagramUser?.fullName,
              username: instagramUser?.username,
              profileImageUrl: instagramUser?.profileImageUrl,
              isFetched: isDefined(instagramUser),
              isFetching: isFetchingDefaultInstagramAccount,
              isStale: isInstagramStale || (isFacebookStale ?? false),
            };
          }
          case SocialNetwork.Pinterest: {
            return {
              bio: pinterestUser?.about,
              followerCount: pinterestUser?.followerCount,
              fullName: pinterestUser?.fullName,
              username: pinterestUser?.username,
              profileImageUrl: pinterestUser?.profileImageUrl,
              isFetched: isDefined(pinterestUser),
              isFetching:
                isFetchingPinterestUser || isFetchingPinterestSettings,
              isStale: isPinterestStale,
            };
          }
          case SocialNetwork.TikTok: {
            return {
              followerCount: tiktokUser?.followersCount,
              fullName: tiktokUser?.displayName,
              username: tiktokUser?.username,
              profileImageUrl: tiktokUser?.profileImage,
              isFetched: isDefined(tiktokUser),
              isFetching: isFetchingTikTokUser || isFetchingTikTokSettings,
              isStale: isTikTokStale,
            };
          }
          case SocialNetwork.Twitter: {
            return {
              bio: twitterUser?.description,
              followerCount:
                twitterUser?.userMetrics?.publicMetrics?.followersCount,
              fullName: twitterUser?.name,
              username: twitterUser?.screenName,
              profileImageUrl: twitterUser?.profileImageUrl,
              isFetched: isDefined(twitterUser),
              isFetching: isFetchingTwitterUser || isFetchingTwitterSettings,
              // Twitter connections don't have a stale status
              isStale: false,
            };
          }
          case SocialNetwork.YouTube: {
            return {
              bio: youtubeUserInfo?.description,
              followerCount: youtubeUserInfo?.subscriberCount,
              isConnected: !!youtubeConnectionInfo?.isConnected,
              isFetched:
                isDefined(youtubeUserInfo) &&
                !isLoadingYouTubeUserInfo &&
                isDefined(youtubeConnectionInfo) &&
                !isLoadingYouTubeConnectionInfo,
              isFetching:
                isLoadingYouTubeConnectionInfo || isLoadingYouTubeUserInfo,
              isStale: !!youtubeConnectionInfo?.hasStaleCredentials,
              network: SocialNetwork.YouTube,
              profileImageUrl: youtubeUserInfo?.thumbnailUrl,
              username: youtubeUserInfo?.handle,
            };
          }
          default: {
            console.error(
              `User info not supported for network ${socialNetwork}`,
            );
            return {
              bio: undefined,
              followerCount: undefined,
              fullName: undefined,
              username: '',
              profileImageUrl: undefined,
              isFetched: true,
              isFetching: false,
              isStale: false,
            };
          }
        }
      };
      return socialNetworks.map((network) => {
        const socialNetworkUserInfo = getSocialNetworkUserInfo(network);

        return {
          network,
          ...socialNetworkUserInfo,
        };
      });
    },
  );
